import { Select, Button, Col, Form, Row, Upload, Card } from "antd";
import useNotification from "utils/hooks/useNotification";
import useFooterStore from "views/hooks/useFooterStore";
import ImportPerCode from "./components/ImportPerCode";
import ImportErrors from "./components/ImportErrors";
import ClientInfoGuide from "./components/ClientInfoGuide";
import CategoryGuide from "./components/CategoryGuide";
import ProductGuide from "./components/ProductGuide";
import { getFormError } from "utils/getFormError";
import { Content } from "antd/es/layout/layout";
import useImportApi from "./hooks/useImportApi";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";
import { useEffect, useState } from "react";
import Footer from "components/Footer/Footer";
import React from "react";
import { FaInfoCircle, FaUpload } from "react-icons/fa";
import CardTitle from "components/CardTitle";
import type { UploadChangeParam, UploadFile } from "antd/es/upload/interface";
import type { ImportPerCodeEntry } from "./components/ImportPerCode";

interface ImportFormValues {
  [field: string]: unknown;
  type: string;
  file?: UploadChangeParam<UploadFile> | string;
}

interface ImportMutationVars {
  input: {
    companyId: ID;
    file: File;
  };
}

type ImportMutationFn = (options: {
  variables: ImportMutationVars;
}) => Promise<{ data?: ImportMutationResponse | null }>;

interface ImportMutationResponse {
  importProductsFromFile?: ImportPayload;
  importCategoriesFromFile?: ImportPayload;
  importClientInfo?: ImportPayload;
}

interface ImportPayload {
  success?: boolean;
  errors?: ErrorType[];
  errorsPerCode?: ImportPerCodeEntry[];
}

export default function Import(): React.ReactElement {
  const { t } = useTranslation();
  const urlParams: RouteParams = useParams();

  const [form] = Form.useForm();

  const [importErrors, setImportErrors] = useState<ErrorType[]>();
  const [errorsPerCode, setImportErrorsPerCode] = useState<ImportPerCodeEntry[]>();
  const setSaveLoading = useFooterStore(state => state.setSaveLoading);
  const handleOkSaveButton = useFooterStore(state => state.handleOkSaveButton);
  const setHandleOkSaveButton = useFooterStore(state => state.setHandleOkSaveButton);

  const type = Form.useWatch("type", form);

  const {
    importProductsFromFile,
    importClientInfo,
    importCategoriesFromFile,
    importProductsFromFileLoading,
    importCategoriesFromFileLoading,
    importClientInfoLoading,
    listDefaultFilesToImport,
    listDefaultFiles,
  } = useImportApi();

  const mainLoading =
    importProductsFromFileLoading || importCategoriesFromFileLoading || importClientInfoLoading;
  const { notificationError, notificationSuccessfullyImported } = useNotification();

  useEffect(() => {
    form.setFields([
      {
        name: "type",
        value: "importClientInfo",
      },
    ]);
  }, []);

  useEffect(() => {
    listDefaultFilesToImport();
  }, []);

  const onFinish = async (values: ImportFormValues) => {
    let queryName: ImportMutationFn | undefined;
    let variables: ImportMutationVars;
    const type = values?.type;
    let file: File | undefined;
    if (!values.file || typeof values.file === "string") {
      delete values.file;
    } else {
      file = values.file.file?.originFileObj || values.file.fileList?.[0]?.originFileObj;
    }

    if (!file) return;

    variables = {
      input: {
        companyId: urlParams.companyId,
        file,
      },
    };

    if (values.type === "importProductsFromFile") {
      queryName = importProductsFromFile;
    } else if (values.type === "importCategoriesFromFile") {
      queryName = importCategoriesFromFile;
      variables = {
        input: {
          companyId: urlParams.companyId,
          file,
        },
      };
    } else if (values.type === "importClientInfo") {
      queryName = importClientInfo;
      variables = {
        input: {
          companyId: urlParams.companyId,
          file,
        },
      };
    }
    let errors: ErrorType[] | undefined = [];

    try {
      const response = await queryName?.({ variables });
      if (values.type === "importClientInfo" || values.type === "importProductsFromFile") {
        errors = (response?.data as Record<string, ImportPayload | undefined> | undefined)?.[type]
          ?.errors;
        const errorsPerCode = (
          response?.data as Record<string, ImportPayload | undefined> | undefined
        )?.[type]?.errorsPerCode;
        setImportErrorsPerCode(errorsPerCode);
      } else if (values.type === "importCategoriesFromFile") {
        errors = (response?.data as Record<string, ImportPayload | undefined> | undefined)?.[type]
          ?.errors;
        setImportErrors(errors);
      }

      if (
        (response?.data as Record<string, ImportPayload | undefined> | undefined)?.[type]?.success
      ) {
        notificationSuccessfullyImported(t("File"));
        form.resetFields(["file"]);
      }

      if (errors && errors.length > 0) {
        const fieldsData = getFormError(errors, values);
        notificationError(errors);
        form.setFields(fieldsData);
      }
    } finally {
      setHandleOkSaveButton(false);
      setSaveLoading(false);
    }
  };

  useEffect(() => {
    if (handleOkSaveButton) {
      handleOk();
    }
  }, [handleOkSaveButton]);

  const handleOk = () => {
    form.submit();
    setSaveLoading(true);
  };
  const onFinishFailed = ({
    errorFields,
  }: {
    errorFields: { name: (string | number)[]; errors: string[] }[];
  }) => {
    if (errorFields) {
      setHandleOkSaveButton(false);
      setSaveLoading(false);
    }
  };

  const handleOnClickModel = async () => {
    let url;
    if (type === "importClientInfo") {
      url = listDefaultFiles?.find(item => item?.slug === "client_1");
    } else if (type === "importProductsFromFile") {
      url = listDefaultFiles?.find(item => item?.slug === "products_1");
    } else if (type === "importCategoriesFromFile") {
      url = listDefaultFiles?.find(item => item?.slug === "categories_1");
    }

    if (url) {
      const link = document.createElement("a");
      link.href = url?.file;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  };

  return (
    <>
      <Content className="layout-card-content ">
        <Form
          name="importForm"
          layout="vertical"
          form={form}
          onFinish={onFinish}
          onFinishFailed={onFinishFailed}
          autoComplete="off"
        >
          <Row gutter={[16, 16]}>
            <Col span={12}>
              <Card
                title={<CardTitle icon={<FaUpload />} title={t("Import")} />}
                loading={mainLoading}
                className="antd-newrai-card__table"
              >
                <Col span={8}>
                  <Form.Item label={t("Type")} name="type">
                    <Select
                      options={[
                        { label: t("Clients"), value: "importClientInfo" },
                        { label: t("Products / Services"), value: "importProductsFromFile" },
                        { label: t("Categories"), value: "importCategoriesFromFile" },
                      ]}
                    />
                  </Form.Item>
                </Col>
                <Col xs={24} sm={12}>
                  <Form.Item
                    rules={[{ required: true, message: t("This field is required") }]}
                    name="file"
                    extra={
                      <Row justify={"start"} className="import-info">
                        <Col span={24} style={{ fontSize: 12 }}>
                          {t("accepted_format")}{" "}
                        </Col>
                        <Col span={24} style={{ fontSize: 12 }}>
                          {t("The file must not contain more than 2000 lines")}
                        </Col>
                        <Col span={12}>
                          <Button
                            onClick={handleOnClickModel}
                            style={{ padding: 0, fontSize: 12 }}
                            type="link"
                          >
                            {t("Model.xlsx")}
                          </Button>
                        </Col>
                      </Row>
                    }
                  >
                    <Upload
                      accept=".csv, application/vnd.openxmlformats-officedocument.spreadsheetml.sheet, application/vnd.ms-excel"
                      maxCount={1}
                      customRequest={({ onSuccess }) => {
                        setTimeout(() => {
                          onSuccess?.("ok");
                        }, 0);
                      }}
                    >
                      <Button>{t("Select a file")}</Button>
                    </Upload>
                  </Form.Item>
                </Col>

                <ImportErrors importErrors={importErrors} />
                <ImportPerCode errorsPerCode={errorsPerCode} />
              </Card>
            </Col>
            <Col span={12}>
              <Card
                title={<CardTitle icon={<FaInfoCircle />} title={t("Helpful information")} />}
                loading={mainLoading}
                className="antd-newrai-card__table"
              >
                {type === "importClientInfo" && <ClientInfoGuide />}
                {type === "importCategoriesFromFile" && <CategoryGuide />}
                {type === "importProductsFromFile" && <ProductGuide />}
              </Card>
            </Col>
          </Row>
        </Form>
      </Content>
      <Footer />
    </>
  );
}
